import { computed, ref } from 'vue';
import { getIsPosthogEnabled, posthog } from 'boot/posthog';

/** @typedef {import('vue').Ref} Ref */
/** @typedef {import('vue').ComputedRef} ComputedRef */

const flagsReady = ref(false);
const flags = ref({});

let initialized = false;
let unsubscribeFeatureFlags = null;

/**
 * Reset shared feature flag state.
 *
 * @returns {void}
 */
export function resetFeatureFlagsState() {
  if (typeof unsubscribeFeatureFlags === 'function') {
    unsubscribeFeatureFlags();
    unsubscribeFeatureFlags = null;
  }

  flagsReady.value = false;
  flags.value = {};
  initialized = false;
}

/**
 * Sync the local reactive flag map from PostHog.
 *
 * @returns {void}
 */
function syncFlags() {
  const keys = posthog.featureFlags?.getFlags?.() || [];

  flags.value = keys.reduce((acc, key) => {
    acc[key] = Boolean(posthog.isFeatureEnabled(key));
    return acc;
  }, {});
}

/**
 * Initialize shared feature flag state and PostHog subscriptions once.
 *
 * @returns {void}
 */
function init() {
  if (initialized) {
    return;
  }

  if (!getIsPosthogEnabled()) {
    flags.value = {};
    flagsReady.value = false;
    initialized = true;
    return;
  }

  flagsReady.value = false;

  unsubscribeFeatureFlags = posthog.onFeatureFlags(() => {
    syncFlags();
    flagsReady.value = true;
  });

  initialized = true;
}

/**
 * Shared composable for PostHog feature flags.
 *
 * @returns {{
 *   flagsReady: Ref<boolean>,
 *   flags: Ref<Record<string, boolean>>,
 *   isFeatureEnabled: (flagKey: string) => boolean,
 *   useFeatureEnabled: (flagKey: string) => ComputedRef<boolean>,
 *   getFeatureFlag: (flagKey: string) => unknown,
 *   reloadFeatureFlags: () => void,
 * }}
 */
// eslint-disable-next-line import/prefer-default-export
export function useFeatureFlags() {
  init();

  const isPosthogEnabled = computed(() => getIsPosthogEnabled());

  /**
   * Return the current boolean value of a feature flag.
   *
   * Returns false when PostHog is disabled or feature flags are not loaded yet.
   *
   * @param {string} flagKey
   * @returns {boolean}
   */
  const isFeatureEnabled = (flagKey) => {
    if (!isPosthogEnabled.value || !flagsReady.value) {
      return false;
    }

    return Boolean(flags.value[flagKey]);
  };

  /**
   * Return the raw PostHog value for a feature flag.
   *
   * Returns null when PostHog is disabled or feature flags are not loaded yet.
   *
   * @param {string} flagKey
   * @returns {null | unknown}
   */
  const getFeatureFlag = (flagKey) => {
    if (!isPosthogEnabled.value || !flagsReady.value) {
      return null;
    }

    return posthog?.getFeatureFlag?.(flagKey);
  };

  /**
   * Return a computed boolean ref for a feature flag.
   *
   * @param {string} flagKey
   * @returns {ComputedRef<boolean>}
   */
  const useFeatureEnabled = (flagKey) => computed(() => isFeatureEnabled(flagKey));

  /**
   * Reload feature flags from PostHog.
   *
   * @returns {void}
   */
  const reloadFeatureFlags = () => {
    if (!isPosthogEnabled.value) {
      return;
    }

    flagsReady.value = false;
    posthog?.reloadFeatureFlags?.();
  };

  return {
    flagsReady,
    flags,
    isFeatureEnabled,
    useFeatureEnabled,
    getFeatureFlag,
    reloadFeatureFlags,
  };
}
